import { get } from './get';

class PageContext {

    constructor() {
        this.props = {
            EVEREST_DEPLOY_ENV: process.env.EVEREST_DEPLOY_ENV?.trim(),
            NODE_ENV: process.env.NODE_ENV,
            isClient: process.env.CLIENT,
            everestTheme: process.env.EVEREST_THEME?.trim()
        };
    }

    /**
     * Shortcut method for returning the current vertical name
     * @returns {String} - The vertical name
     */
    getVertical = () => {
        if (!this.props.models) {
            throw Error('Trying to access vertical before PageSchemaModel has been set on context');
        }
        return this.props.models.pageSchemaModel.getSettings().vertical;
    };

    /**
     * Add / overwrite the props on this.props with the passed props object
     * @param {Object} props - The current set of props
     */
    setProps = (props) => {
        Object.assign(this.props, props);
    };

    /**
     * Add / overwrite a specific props
     * @param {String} id - the id of the prop to set
     * @param {*} value - The value of the prop to set
     */
    setProp = (id, value) => {
        if (typeof id !== 'string') {
            throw Error(`Invalid id primitive type passed to setProp: ${typeof id}`);
        }
        this.props[id] = value;
    };

    /**
     * Returns the current set of props.
     * @returns {Object} - The current set of props
     */
    getProps = () => {
        return this.props;
    };

    /**
     * Determines if you are in NODE_ENV development
     * @param {Array} allowedEnvs - An optional list of envs to consider as being in dev mode - sometimes we don't want
     * to include uat as dev env
     * @returns {Boolean} Whether or not in dev mode.
     */
    inDevMode = (allowedEnvs = ['local', 'dev', 'ext']) => {
        return (allowedEnvs || ['local', 'dev', 'ext']).includes(this.props.EVEREST_DEPLOY_ENV);
    };

    /**
     * Tooling mode: Enables tools such as copy schema, or other tools that are created.
     * @returns {Boolean} True or False
     */
    inToolMode = () => {
        return this.inDevMode() && !!get(this.props, 'location.query.tooling');
    };

}

// export the instance so it acts as a singleton
export const pageContext = new PageContext();

/**
 * Apply vertical specific styles to a component.
 * The CSS is pulled from the builder props.
 * @param {Object} styles - Component styles object
 * @param {String} componentName - Should match component name but in snake case, see naming conventions http://confluence:8090/display/CM/Everest+-+File+Name+Conventions
 * @throws {Error} Invalid arguments.
 * @returns {Object} The merged styles
 */
export const applyVerticalStyles = (styles, componentName) => {
    // get vertical styles
    const verticalStyles = get(pageContext.getProps(), `verticalCss.${componentName}`);

    // validate vertical styles
    if (typeof verticalStyles !== 'object') {
        return styles;
    }

    // validate component styles
    if (typeof styles !== 'object') {
        return verticalStyles;
    }

    // merge styles
    return { ...styles, ...verticalStyles };
};

/**
 * Wrapper function to get verticalComponents from pageContext
 * @returns {Object} verticalComponents - The set of vertical specific components
 */
export const getVerticalComponents = () => {
    return get(pageContext.getProps(), 'verticalComponents');
};
